import { median, standardDeviation } from "@/lib/stats";

/**
 * Institutionelle Peer-Group-Engine (siehe backlog.md -> "Institutionelle
 * Peer-Group-Engine / Relative-Value"). Reine, unit-getestete Statistik-Funktionen -
 * kein I/O, kein Zufall. Ausbau des bestehenden, manuellen Peer-Vergleichs
 * (components/peer-compare.tsx): Nutzer suchen Vergleichsaktien weiterhin selbst
 * (kein automatischer Markt-Screener verfuegbar, siehe docs/features/peer-group-engine.md),
 * diese Engine wertet die manuell zusammengestellte Gruppe danach quantitativ aus.
 */

export type MetricPolarity = "higher-is-better" | "lower-is-better";

export interface PeerMetricInput {
  symbol: string;
  value: number | undefined;
}

export interface ZScoreResult {
  symbol: string;
  value: number | undefined;
  /** Polaritaets-korrigiert: positiver Z-Score bedeutet immer "besser als die Peer-Gruppe",
   * unabhaengig davon, ob hohe oder niedrige Rohwerte fundamental gut sind. */
  zScore: number | undefined;
}

/**
 * Standardisiert eine Kennzahl ueber die Peer-Gruppe (Median + Stichproben-
 * Standardabweichung, siehe Spec). Peers ohne Wert erhalten zScore=undefined statt
 * eines erfundenen Werts. Bei Standardabweichung 0 (alle Werte identisch) erhalten
 * alle vorhandenen Werte zScore 0 - es gibt keine Differenzierung, aber auch keinen
 * Grund für +/-Infinity.
 */
export function computeZScores(
  inputs: PeerMetricInput[],
  polarity: MetricPolarity,
): ZScoreResult[] {
  const values = inputs.map((i) => i.value).filter((v): v is number => v !== undefined);
  const med = median(values);
  const sd = standardDeviation(values);

  return inputs.map(({ symbol, value }) => {
    if (value === undefined || med === undefined) {
      return { symbol, value, zScore: undefined };
    }
    if (sd === undefined || sd === 0) {
      return { symbol, value, zScore: 0 };
    }
    const raw = (value - med) / sd;
    return { symbol, value, zScore: polarity === "higher-is-better" ? raw : -raw };
  });
}

/** Marktkapitalisierungs-Klammer laut Spec: Kandidat darf max. 75 % kleiner und max.
 * 1000 % (11x) groesser sein als die Ziel-Aktie. */
export function isWithinMarketCapBracket(
  targetMarketCap: number,
  candidateMarketCap: number,
  bracket: { minRatio: number; maxRatio: number },
): boolean {
  if (targetMarketCap <= 0 || candidateMarketCap <= 0) return false;
  const ratio = candidateMarketCap / targetMarketCap;
  return ratio >= bracket.minRatio && ratio <= bracket.maxRatio;
}

export type MarketCapClass = "large" | "mid" | "small";

/** Ordnet eine Marktkapitalisierung einer groben Groessenklasse zu (backlog.md ->
 * "Peer-Group zusaetzlich nach Marktkapitalisierungsklasse verfeinern"). `thresholds`
 * wird als Parameter uebergeben statt PEER_GROUP direkt zu importieren - gleiches Prinzip
 * wie isWithinMarketCapBracket() oben, haelt diese Datei frei von Config-Kopplung und
 * unabhaengig testbar. undefined statt einer erfundenen Klasse bei fehlender/ungueltiger
 * Marktkapitalisierung. */
export function classifyMarketCap(
  marketCap: number | undefined,
  thresholds: { large: number; mid: number },
): MarketCapClass | undefined {
  if (marketCap === undefined || marketCap <= 0) return undefined;
  if (marketCap >= thresholds.large) return "large";
  if (marketCap >= thresholds.mid) return "mid";
  return "small";
}

/** true nur, wenn Ziel und Kandidat in DERSELBEN Groessenklasse liegen - zusaetzlich zur
 * kontinuierlichen isWithinMarketCapBracket()-Ratio-Klammer, siehe deren gemeinsamen
 * PEER_GROUP.marketCapClassThresholds-Kommentar fuer ein konkretes Beispiel, in dem die
 * Ratio-Klammer allein nicht reicht. undefined (nicht false), wenn eine der beiden
 * Marktkapitalisierungen nicht klassifizierbar ist - Aufrufer entscheidet bewusst selbst,
 * ob das dann als Ausschluss oder als "kein Widerspruch" behandelt wird, statt hier
 * stillschweigend ein Nein zu erfinden. */
export function isSameMarketCapClass(
  targetMarketCap: number | undefined,
  candidateMarketCap: number | undefined,
  thresholds: { large: number; mid: number },
): boolean | undefined {
  const targetClass = classifyMarketCap(targetMarketCap, thresholds);
  const candidateClass = classifyMarketCap(candidateMarketCap, thresholds);
  if (targetClass === undefined || candidateClass === undefined) return undefined;
  return targetClass === candidateClass;
}

/** Sortiert Kandidaten nach Naehe zur Ziel-Marktkapitalisierung und kappt auf maxCount -
 * "Top 8" aus der Spec, um visuelles Overcrowding zu vermeiden. */
export function rankPeersByMarketCapProximity<T extends { marketCap?: number }>(
  targetMarketCap: number,
  candidates: T[],
  maxCount: number,
): T[] {
  return [...candidates]
    .filter((c): c is T & { marketCap: number } => c.marketCap !== undefined)
    .sort(
      (a, b) =>
        Math.abs(a.marketCap - targetMarketCap) - Math.abs(b.marketCap - targetMarketCap),
    )
    .slice(0, maxCount);
}

export type TargetFitWeights = Record<string, number>;

export interface TargetFitResult {
  symbol: string;
  /** 0-100. Gewichteter Mittelwert der verfuegbaren Z-Scores, auf eine intuitive
   * Prozent-Skala abgebildet (siehe mapZScoreToPercent). */
  score: number;
}

/** Bildet einen Z-Score (typischerweise -3..+3) auf eine 0-100-Skala ab. Design-
 * Entscheidung, keine statistische Notwendigkeit: 0 -> 50 (Median der Peer-Gruppe),
 * +/-3 Standardabweichungen -> 100/0, dazwischen linear, an den Raendern geklemmt. */
export function mapZScoreToPercent(zScore: number): number {
  return Math.max(0, Math.min(100, 50 + zScore * (50 / 3)));
}

/**
 * Gewichteter Gesamt-Score je Aktie ueber mehrere Kennzahlen hinweg ("Target-Fit",
 * siehe Spec-Beispiel: Cashflow-Fokus gewichtet Dividendenrendite + FCF-Yield zu 60 %).
 * Fehlende Kennzahlen einer Aktie werden aus der Gewichtssumme ausgeschlossen und die
 * uebrigen Gewichte anteilig renormalisiert - kein Erfinden fehlender Werte.
 */
export function computeTargetFitScores(
  metricZScoresBySymbol: Map<string, Record<string, number | undefined>>,
  weights: TargetFitWeights,
): TargetFitResult[] {
  const results: TargetFitResult[] = [];
  for (const [symbol, metrics] of metricZScoresBySymbol) {
    let weightedSum = 0;
    let weightTotal = 0;
    for (const [metricKey, weight] of Object.entries(weights)) {
      const z = metrics[metricKey];
      if (z === undefined) continue;
      weightedSum += z * weight;
      weightTotal += weight;
    }
    if (weightTotal === 0) continue;
    const avgZ = weightedSum / weightTotal;
    results.push({ symbol, score: mapZScoreToPercent(avgZ) });
  }
  return results;
}
